'use client';
import { LearnData } from '@osmosis/shared/types/learndb/learnData';

import axios from 'axios';
import React, { PropsWithChildren, useContext } from 'react';
import { useQuery } from 'react-query';
import { getLearnPage } from '../lib/frontend/learn';
import { usePagesContext } from './PagesProvider';
import { LanguageCode } from '@elsevier-health/osmosis-shared/languages';
import { useUserContext } from './UserContext';

type LearnDataContext = {
  learnData?: LearnData;
  isLoading: boolean;
  isEmbedRemovalActive: boolean;
};

const LearnDataContext = React.createContext<LearnDataContext>({
  isLoading: true,
  isEmbedRemovalActive: false,
});

/**
 * Provides the LearnData context.
 * @param props - The component props.
 */
export const LearnDataProvider = ({ children }: PropsWithChildren) => {
  const {
    query,
    params: { concept, lang: language = 'en' },
  } = usePagesContext();
  const { user } = useUserContext();

  const { data: learnData, isLoading } = useQuery(['learnData', language], () =>
    getLearnPage(axios.get, {
      query: [concept].flat().join('/') as string,
      from: query.from as string,
      language: language as LanguageCode,
      nocache: query.nocache as string,
      productID: user?.productID ?? undefined,
    }),
  );
  const isEmbedRemovalActive = learnData?.isEmbedRemovalActive ?? false;

  return (
    <LearnDataContext.Provider
      value={
        {
          learnData,
          isLoading,
          isEmbedRemovalActive,
        } as LearnDataContext
      }
    >
      {children}
    </LearnDataContext.Provider>
  );
};

/**
 * Hook to access the user context.
 */
export const useLearnData = (): LearnDataContext =>
  useContext(LearnDataContext);
